import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiConfiguration } from '../api-configuration';
import { BaseService } from '../base-service';
import {
  CreateProgramPayload,
  ProgramAgeGroupOption,
  ProgramListFilters,
  ProgramManagement,
  ProgramPaginatedResult,
  ProgramPaginationMeta,
  ProgramStatus,
  UpdateProgramPayload,
} from '../models/programs-manual.models';

interface ApiEnvelope<T> {
  status: string;
  message?: string;
  data: T;
}

interface ProgramsListData {
  items: ProgramManagement[];
  pagination?: Partial<ProgramPaginationMeta>;
}

@Injectable({ providedIn: 'root' })
export class ProgramsManualService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  list(
    academyId: number | string,
    params: ProgramListFilters = {}
  ): Observable<ProgramPaginatedResult<ProgramManagement>> {
    return this.http
      .get<ApiEnvelope<ProgramsListData>>(
        `${this.rootUrl}/api/v1/academy/${academyId}/programs`,
        { params: this.buildParams(params as Record<string, unknown>) }
      )
      .pipe(map((response) => this.normalizeList(this.unwrap(response))));
  }

  create(academyId: number | string, payload: CreateProgramPayload): Observable<ProgramManagement> {
    return this.http
      .post<ApiEnvelope<ProgramManagement>>(
        `${this.rootUrl}/api/v1/academy/${academyId}/programs`,
        payload
      )
      .pipe(map((response) => this.unwrap(response)));
  }

  get(academyId: number | string, programId: number): Observable<ProgramManagement> {
    return this.http
      .get<ApiEnvelope<ProgramManagement>>(
        `${this.rootUrl}/api/v1/academy/${academyId}/programs/${programId}`
      )
      .pipe(map((response) => this.unwrap(response)));
  }

  update(
    academyId: number | string,
    programId: number,
    payload: UpdateProgramPayload
  ): Observable<ProgramManagement> {
    return this.http
      .put<ApiEnvelope<ProgramManagement>>(
        `${this.rootUrl}/api/v1/academy/${academyId}/programs/${programId}`,
        payload
      )
      .pipe(map((response) => this.unwrap(response)));
  }

  updateStatus(
    academyId: number | string,
    programId: number,
    status: ProgramStatus
  ): Observable<ProgramManagement> {
    return this.http
      .patch<ApiEnvelope<ProgramManagement>>(
        `${this.rootUrl}/api/v1/academy/${academyId}/programs/${programId}/status`,
        { status }
      )
      .pipe(map((response) => this.unwrap(response)));
  }

  delete(academyId: number | string, programId: number): Observable<void> {
    return this.http
      .delete<ApiEnvelope<unknown>>(`${this.rootUrl}/api/v1/academy/${academyId}/programs/${programId}`)
      .pipe(map(() => void 0));
  }

  getAgeGroups(academyId: number | string): Observable<ProgramAgeGroupOption[]> {
    return this.http
      .get<ApiEnvelope<ProgramAgeGroupOption[]>>(
        `${this.rootUrl}/api/v1/lookups/academy/${academyId}/age-groups`
      )
      .pipe(map((response) => this.unwrap(response) ?? []));
  }

  private unwrap<T>(response: ApiEnvelope<T>): T {
    return response?.data as T;
  }

  private buildParams(params: Record<string, unknown>): HttpParams {
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }

      httpParams = httpParams.set(key, String(value));
    });

    return httpParams;
  }

  private normalizeList(payload: ProgramsListData | undefined): ProgramPaginatedResult<ProgramManagement> {
    const fallbackMeta: ProgramPaginationMeta = {
      current_page: 1,
      last_page: 1,
      per_page: 20,
      total: 0,
    };

    if (!payload) {
      return { items: [], meta: fallbackMeta };
    }

    return {
      items: payload.items ?? [],
      meta: {
        current_page: payload.pagination?.current_page ?? fallbackMeta.current_page,
        last_page: payload.pagination?.last_page ?? fallbackMeta.last_page,
        per_page: payload.pagination?.per_page ?? fallbackMeta.per_page,
        total: payload.pagination?.total ?? (payload.items?.length ?? fallbackMeta.total),
      },
    };
  }
}
